import { useCallback, useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';

type Axis = 'x' | 'y';

/** Dimensione persistita (px) con drag handle. */
export function usePersistedSize(storageKey: string, fallback: number, min: number, max: number) {
  const [size, setSize] = useState(() => {
    try {
      const v = Number(localStorage.getItem(storageKey));
      if (Number.isFinite(v) && v >= min && v <= max) return v;
    } catch {
      /* ignore */
    }
    return fallback;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(size));
    } catch {
      /* ignore */
    }
  }, [size, storageKey]);

  const startDrag = useCallback(
    (axis: Axis, invert = false) =>
      (e: ReactMouseEvent) => {
        e.preventDefault();
        const start = axis === 'x' ? e.clientX : e.clientY;
        const startSize = size;
        const onMove = (ev: MouseEvent) => {
          const delta = (axis === 'x' ? ev.clientX : ev.clientY) - start;
          const next = Math.min(max, Math.max(min, startSize + (invert ? -delta : delta)));
          setSize(next);
        };
        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };
        document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      },
    [size, min, max],
  );

  return { size, setSize, startDrag };
}

export function Splitter({
  orientation,
  onMouseDown,
}: {
  orientation: 'vertical' | 'horizontal';
  onMouseDown: (e: ReactMouseEvent) => void;
}) {
  return (
    <div
      className={`splitter splitter-${orientation}`}
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation={orientation}
    />
  );
}
